import { useState } from "react"
import GameHistoryChart from "./GameHistoryChart"

interface AnalyticsTabProps {
  gameHistory: IMinifiedGameHistory
}

type ChartType = "money" | "properties" | "cards" | "stock"

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ gameHistory }) => {
  const [selectedChart, setSelectedChart] = useState<ChartType>("money")

  const chartOptions = [
    { value: "money", label: "Money" },
    { value: "properties", label: "Number of Properties" },
    { value: "cards", label: "Number of Cards" },
    { value: "stock", label: "Stock Value" },
  ]

  const getChartConfig = (chartType: ChartType) => {
    switch (chartType) {
      case "money":
        return {
          title: "Player Money Over Time",
          yAxisLabel: "Money ($)",
        }
      case "properties":
        return {
          title: "Player Properties Over Time",
          yAxisLabel: "Properties",
        }
      case "cards":
        return {
          title: "Player Cards Over Time",
          yAxisLabel: "Cards",
        }
      case "stock":
        return {
          title: "Player Stock Value Over Time",
          yAxisLabel: "Stock Value",
        }
      default:
        return {
          title: "Player Money Over Time",
          yAxisLabel: "Money ($)",
        }
    }
  }

  const renderChart = () => {
    const config = getChartConfig(selectedChart)
    return (
      <GameHistoryChart
        gameHistory={gameHistory}
        dataType={selectedChart}
        title={config.title}
        yAxisLabel={config.yAxisLabel}
      />
    )
  }

  return (
    <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-6">
      <h3 className="text-xl font-semibold text-white mb-4">Analytics</h3>

      {/* Game Statistics */}
      <div className="mb-6 bg-gray-900/50 border border-gray-600 rounded-lg p-4">
        <h4 className="text-white font-medium mb-2">Game Statistics</h4>
        <div className="text-gray-300 text-sm">
          <p>Total Events: {gameHistory.history.length}</p>
          <p>Max Turns: {gameHistory.constantData.settings.maxTurns}</p>
          <p>Players: {gameHistory.constantData.players.length}</p>
        </div>
      </div>

      {/* Chart Selector */}
      <div className="mb-6">
        <label
          htmlFor="chart-selector"
          className="block text-white font-medium mb-2"
        >
          Select Chart Type
        </label>
        <select
          id="chart-selector"
          value={selectedChart}
          onChange={(e) => setSelectedChart(e.target.value as ChartType)}
          className="w-full md:w-64 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {chartOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Chart Section - Full Width */}
      <div className="mb-6">{renderChart()}</div>

      {/* Additional Analytics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900/50 border border-gray-600 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2">Player Performance</h4>
          <div className="text-gray-300 text-sm">
            <p>Player performance analytics will be implemented here...</p>
          </div>
        </div>

        <div className="bg-gray-900/50 border border-gray-600 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2">Property Analysis</h4>
          <div className="text-gray-300 text-sm">
            <p>
              Property ownership and transaction analytics will be shown here...
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsTab
